import { StepType } from "@redotech/redo-model/advanced-flow/common";
import { ContactChannel } from "@redotech/redo-model/advanced-flow/rules-v3/schemas";
import {
  ErrorHandlingStrategy,
  QueryOperator,
  RuleActionType,
  RuleConditionType,
  RuleTriggerType,
} from "@redotech/redo-model/advanced-flow/rules/types";
import { SchemaType } from "@redotech/redo-model/advanced-flow/schemas/schemas";
import { Schema } from "effect";
import { makeBind } from "../field-binding";
import { DefaultRule } from "../types";

/**
 * Shape of this rule's `configurations` JSONB blob. The merchant fills in
 * a list of exact email addresses or domains; any inbound email from a
 * sender on the list stops the message from appending to an existing ticket.
 */
export const StopTicketContinuationOnAutoSplitSenderConfigSchema =
  Schema.Struct({ autoSplitSenders: Schema.Array(Schema.String) });

export type StopTicketContinuationOnAutoSplitSenderConfig =
  typeof StopTicketContinuationOnAutoSplitSenderConfigSchema.Type;

const bind = makeBind<StopTicketContinuationOnAutoSplitSenderConfig>();

/**
 * Default rule: Stop ticket continuation for auto-split senders
 *
 * When an email arrives (gmail/outlook/customEmail) from a sender on the
 * merchant's auto-split list, stop appending the message to the existing
 * ticket so it opens as a new conversation instead. Off by default —
 * merchants add addresses and the rule enables automatically.
 *
 * Runs alongside the archive rule (`sortIndex: 2`) so both MESSAGE_RECEIVED
 * default rules can coexist.
 */
export const stopTicketContinuationOnAutoSplitSenderRule = {
  id: "default-stop-ticket-continuation-on-auto-split-sender",
  name: "Stop ticket continuation for auto-split senders",
  description:
    "When an email arrives from a sender on the auto-split list, stop appending to the existing ticket.",
  hidden: false,
  triggerType: RuleTriggerType.MESSAGE_RECEIVED,

  configSchema: StopTicketContinuationOnAutoSplitSenderConfigSchema,

  defaultConfig: {
    defaultRuleId: "default-stop-ticket-continuation-on-auto-split-sender",
    enabled: false,
    sortIndex: 2,
    configurations: { autoSplitSenders: [] },
  },

  template: {
    name: "Stop ticket continuation for auto-split senders",
    description:
      "When an email arrives from a sender on the auto-split list, stop appending to the existing ticket.",
    enabled: true,
    schemaType: SchemaType.RULES_V3,
    steps: [
      {
        type: StepType.TRIGGER,
        id: "trigger",
        schemaType: SchemaType.RULES_V3,
        category: "Rules",
        key: RuleTriggerType.MESSAGE_RECEIVED,
        nextId: "conditions",
      },
      {
        type: StepType.RULE_CONDITIONS_V3,
        id: "conditions",
        conditionGroups: {
          operator: "AND",
          conditions: [
            {
              conditionType: RuleConditionType.TICKET_CHANNEL,
              channels: ["gmail", "outlook", "customEmail"],
              queryOperator: QueryOperator.IS,
            },
            {
              conditionType: RuleConditionType.SENT_FROM,
              channel: ContactChannel.EMAIL,
              values: bind("autoSplitSenders", []),
              queryOperator: QueryOperator.IS_EXACTLY,
            },
          ],
        },
        nextTrueId: "actions",
      },
      {
        type: StepType.RULE_ACTIONS_V3,
        id: "actions",
        actions: [{ actionType: RuleActionType.STOP_TOPIC_CONTINUATION }],
        errorHandlingStrategy: ErrorHandlingStrategy.FAIL_FAST,
      },
    ],
  },
} as const satisfies DefaultRule<
  string,
  StopTicketContinuationOnAutoSplitSenderConfig
>;
